import React, { useRef, useState } from 'react';
import { SendHorizontal, Loader2, AtSign, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { initialsOf } from '@/components/contacts/ContactCard';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { FileUploadButton } from '@/components/FileUpload';
import { AttachmentList } from '@/components/AttachmentList';

export const Composer = ({ members, me, onSend, placeholder, lang, disabled, channelId }) => {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState(null);
  const ref = useRef(null);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    const match = before.match(/(?:^|\s)@([^\s@]*)$/);
    setSuggest(match ? { query: match[1].toLowerCase(), start: caret - match[1].length - 1 } : null);
  };
  const candidates = suggest ? members.filter((m) => (m.full_name || m.email).toLowerCase().includes(suggest.query)).slice(0, 6) : [];
  const pick = (m) => {
    const name = m.full_name || m.email;
    const after = text.slice(ref.current?.selectionStart ?? text.length);
    const next = `${text.slice(0, suggest.start)}@${name} ${after}`;
    setText(next);
    setMentions((prev) => (prev.find((x) => x.user_id === m.user_id) ? prev : [...prev, m]));
    setSuggest(null);
    setTimeout(() => ref.current?.focus(), 0);
  };
  const send = async () => {
    const body = text.trim();
    if ((!body && !attachments.length) || busy) return;
    setBusy(true);
    const ids = mentions.filter((m) => body.includes(`@${m.full_name || m.email}`)).map((m) => m.user_id);
    const ok = await onSend(body, ids, attachments.map((a) => a.id));
    if (ok) { setText(''); setMentions([]); setAttachments([]); }
    setBusy(false);
    ref.current?.focus();
  };
  const onKey = (e) => {
    if (suggest && candidates.length && (e.key === 'Enter' || e.key === 'Tab')) { e.preventDefault(); pick(candidates[0]); return; }
    if (e.key === 'Escape') setSuggest(null);
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };
  return (
    <div className="relative border-t border-border bg-white px-4 py-3" data-testid="composer">
      {suggest && candidates.length > 0 && (
        <div className="absolute bottom-full left-4 mb-1 w-64 rounded-xl border border-border bg-white p-1 shadow-lg" data-testid="mention-suggestions">
          {candidates.map((m) => (
            <button type="button" key={m.user_id} onMouseDown={(e) => { e.preventDefault(); pick(m); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted" data-testid={`mention-option-${m.user_id}`}>
              <Avatar className="h-6 w-6"><AvatarFallback className="bg-primary text-primary-foreground text-[9px]">{initialsOf(m.full_name || m.email)}</AvatarFallback></Avatar>
              <span className="truncate">{m.full_name || m.email}</span>{m.user_id === me.id && <span className="text-[10px] text-muted-foreground">({lang === 'id' ? 'Anda' : 'you'})</span>}
            </button>
          ))}
        </div>
      )}
      {attachments.length > 0 && <div className="mb-2 rounded-xl border border-dashed border-border bg-muted/30 px-3 py-2" data-testid="composer-attachments"><AttachmentList attachments={attachments} compact onRemove={(a) => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} /></div>}
      <div className="flex items-end gap-2 rounded-2xl border border-border bg-muted/40 px-3 py-2 focus-within:border-primary focus-within:bg-white transition-colors">
        <FileUploadButton kind="discuss" refId={channelId} multiple variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-muted-foreground" testid="composer-attach-button" disabled={disabled} onUploaded={(att) => setAttachments((prev) => [...prev, att])}><Paperclip size={16} /></FileUploadButton>
        <textarea ref={ref} rows={1} value={text} onChange={onChange} onKeyDown={onKey} disabled={disabled} placeholder={placeholder} className="max-h-40 min-h-[28px] flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-60" style={{ height: 'auto' }} onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; }} data-testid="composer-input" />
        <button type="button" className="rounded-lg p-1 text-muted-foreground hover:text-foreground" title="@mention" onClick={() => { setText((t) => `${t}${t && !t.endsWith(' ') ? ' ' : ''}@`); setSuggest({ query: '', start: text.length + (text && !text.endsWith(' ') ? 1 : 0) }); ref.current?.focus(); }} data-testid="composer-mention-button"><AtSign size={16} /></button>
        <Button size="sm" className="h-8 rounded-xl px-3" onClick={send} disabled={(!text.trim() && !attachments.length) || busy || disabled} data-testid="composer-send-button">{busy ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}</Button>
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground">{lang === 'id' ? 'Enter untuk kirim · Shift+Enter baris baru · @ untuk menyebut rekan · 📎 lampirkan file (maks 20 MB)' : 'Enter to send · Shift+Enter for new line · @ to mention · 📎 attach files (max 20 MB)'}</p>
    </div>
  );
};
